import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VerticalSplit as VerticalSplitIcon,
  HorizontalSplit as HorizontalSplitIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@mui/icons-material";
import { DiffEditor } from "@monaco-editor/react";
import * as Diff from "diff";

export type DiffViewMode = "visual" | "split" | "unified";

interface MarkdownDiffViewerProps {
  original: string;
  modified: string;
  originalTitle?: string;
  modifiedTitle?: string;
  height?: number | string;
  initialMode?: DiffViewMode;
  emptyNotice?: string;
}

export const MarkdownDiffViewer: React.FC<MarkdownDiffViewerProps> = ({
  original = "",
  modified = "",
  originalTitle = "Published Live",
  modifiedTitle = "Current Draft / Edits",
  height = 550,
  initialMode = "visual",
  emptyNotice = "No changes detected. The content is identical to the baseline.",
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [mode, setMode] = useState<DiffViewMode>(initialMode);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Calculate statistics using diffWordsWithSpace
  const { stats, wordDiffParts, hasChanges } = useMemo(() => {
    const orig = original || "";
    const mod = modified || "";

    if (orig === mod) {
      return {
        stats: { additions: 0, deletions: 0, totalChanges: 0 },
        wordDiffParts: [],
        hasChanges: false,
      };
    }

    const diffParts = Diff.diffWordsWithSpace(orig, mod);
    let additions = 0;
    let deletions = 0;

    diffParts.forEach((part) => {
      if (part.added) {
        additions += part.value.trim().split(/\s+/).filter(Boolean).length;
      } else if (part.removed) {
        deletions += part.value.trim().split(/\s+/).filter(Boolean).length;
      }
    });

    return {
      stats: { additions, deletions, totalChanges: additions + deletions },
      wordDiffParts: diffParts,
      hasChanges: additions > 0 || deletions > 0,
    };
  }, [original, modified]);

  // Visual diff styling tokens based on mode
  const addedBg = isDark ? "rgba(46, 160, 67, 0.28)" : "rgba(46, 160, 67, 0.16)";
  const addedColor = isDark ? "#7ee787" : "#1a7f37";
  const removedBg = isDark ? "rgba(248, 81, 73, 0.28)" : "rgba(255, 235, 233, 0.9)";
  const removedColor = isDark ? "#ffa198" : "#cf222e";

  const containerSx = isFullscreen
    ? {
        position: "fixed" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        p: 3,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column" as const,
        boxShadow: 24,
      }
    : {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      };

  return (
    <Box sx={containerSx}>
      {/* Diff Header Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Changes Comparison:
          </Typography>

          {hasChanges ? (
            <>
              <Chip
                label={`+${stats.additions} added`}
                size="small"
                sx={{
                  bgcolor: addedBg,
                  color: addedColor,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  border: `1px solid ${addedColor}33`,
                }}
              />
              <Chip
                label={`-${stats.deletions} removed`}
                size="small"
                sx={{
                  bgcolor: removedBg,
                  color: removedColor,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  border: `1px solid ${removedColor}33`,
                }}
              />
            </>
          ) : (
            <Chip
              icon={<CheckCircleOutlineIcon style={{ fontSize: "1rem" }} />}
              label="Identical Content"
              size="small"
              color="success"
              variant="outlined"
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Mode Switcher */}
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_e, newMode) => {
              if (newMode) setMode(newMode);
            }}
            size="small"
            aria-label="diff mode"
          >
            <ToggleButton value="visual" aria-label="visual diff">
              <Tooltip title="Formatted Article Visual Diff (Track Changes)">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <VisibilityIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 500 }}>
                    Visual
                  </Typography>
                </Stack>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="split" aria-label="split code diff">
              <Tooltip title="Side-by-Side Code Diff">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <VerticalSplitIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 500 }}>
                    Split Code
                  </Typography>
                </Stack>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="unified" aria-label="unified code diff">
              <Tooltip title="Unified Single-Pane Diff">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <HorizontalSplitIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 500 }}>
                    Unified
                  </Typography>
                </Stack>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen Diff"}>
            <IconButton
              size="small"
              onClick={() => setIsFullscreen(!isFullscreen)}
              sx={{ color: "text.secondary" }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Subheader explaining what is compared */}
      <Box
        sx={{
          px: 2,
          py: 0.8,
          bgcolor: isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.01)",
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.75rem",
          color: "text.secondary",
        }}
      >
        <span>
          <strong>Baseline:</strong> {originalTitle}
        </span>
        <span>
          <strong>Modified:</strong> {modifiedTitle}
        </span>
      </Box>

      {/* Diff Content Rendering Area */}
      <Box
        sx={{
          flex: 1,
          height: isFullscreen ? "calc(100vh - 120px)" : height,
          overflow: "auto",
        }}
      >
        {!hasChanges ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              p: 4,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "success.main", mb: 1.5 }} />
            <Typography variant="h6" color="text.primary" gutterBottom>
              Content is Identical
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 450 }}>
              {emptyNotice}
            </Typography>
          </Box>
        ) : mode === "visual" ? (
          /* Visual Track-Changes Mode */
          <Box
            sx={{
              p: 3,
              fontFamily: theme.typography.fontFamily,
              fontSize: "1rem",
              lineHeight: 1.75,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              <span style={{ backgroundColor: addedBg, color: addedColor, padding: "2px 6px", borderRadius: 4, marginRight: 8, fontWeight: 600 }}>
                Green = Added text
              </span>
              <span style={{ backgroundColor: removedBg, color: removedColor, textDecoration: "line-through", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>
                Red = Deleted text
              </span>
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ "& del": { textDecoration: "line-through", opacity: 0.85 } }}>
              {wordDiffParts.map((part, index) => {
                if (part.added) {
                  return (
                    <Box
                      component="ins"
                      key={index}
                      sx={{
                        bgcolor: addedBg,
                        color: addedColor,
                        textDecoration: "none",
                        px: 0.5,
                        py: 0.2,
                        borderRadius: 0.5,
                        fontWeight: 600,
                        borderBottom: `2px solid ${addedColor}`,
                      }}
                    >
                      {part.value}
                    </Box>
                  );
                }
                if (part.removed) {
                  return (
                    <Box
                      component="del"
                      key={index}
                      sx={{
                        bgcolor: removedBg,
                        color: removedColor,
                        textDecoration: "line-through",
                        px: 0.5,
                        py: 0.2,
                        borderRadius: 0.5,
                        opacity: 0.85,
                      }}
                    >
                      {part.value}
                    </Box>
                  );
                }
                return <span key={index}>{part.value}</span>;
              })}
            </Box>
          </Box>
        ) : (
          /* Monaco Diff Editor Mode (Split or Unified) */
          <DiffEditor
            height="100%"
            language="markdown"
            original={original || ""}
            modified={modified || ""}
            theme={isDark ? "vs-dark" : "light"}
            options={{
              renderSideBySide: mode === "split",
              readOnly: true,
              wordWrap: "on",
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              lineNumbers: "on",
              fontSize: 13,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default MarkdownDiffViewer;
